import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Phone } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import AnimatedSection from '../components/AnimatedSection';
import { useLanguage } from '../i18n/pageTranslations';

const Ginecomastia = () => {
  const lang = useLanguage();

  const texts = {
    es: {
      eyebrow: 'Cirugía Corporal',
      title: 'Ginecomastia',
      description: 'Cirugía de reducción de la mama masculina. Mediante liposucción y/o extirpación glandular según el grado, se consigue un pecho masculino definido con alta satisfacción postoperatoria.',
      requestAssessment: 'Solicitar Valoración',
      callNow: 'Llamar Ahora',
      benefitsTitle: 'Beneficios',
      resultsTitle: 'Resultados de la cirugía',
      benefits: [
        'Pecho masculino definido y natural',
        'Alta satisfacción postoperatoria',
        'Técnica adaptada al grado de ginecomastia',
        'Liposucción y/o extirpación glandular según caso',
        'Cicatrices mínimas y discretas',
        'Recuperación en 1-2 semanas',
      ],
      quote: '"La ginecomastia es una de las cirugías con mayor índice de satisfacción. Los pacientes recuperan confianza y bienestar desde las primeras semanas."',
      classificationTitle: 'Clasificación',
      gradesTitle: 'Grados de Ginecomastia',
      grades: [
        { titulo: 'Grado I - Leve', descripcion: 'Pequeño aumento mamario sin exceso de piel. Se trata principalmente con liposucción.' },
        { titulo: 'Grado II - Moderada', descripcion: 'Aumento moderado que requiere liposucción combinada con extirpación de tejido glandular.' },
        { titulo: 'Grado III - Severa', descripcion: 'Aumento significativo con exceso de piel. Puede requerir resección cutánea adicional.' },
      ],
      ctaTitle: '¿Desea más información?',
      ctaDesc: 'Solicite una consulta de valoración personalizada.',
      contact: 'Contactar',
    },
    en: {
      eyebrow: 'Body Surgery',
      title: 'Gynecomastia',
      description: 'Male breast reduction surgery. Through liposuction and/or glandular excision depending on the grade, a defined masculine chest is achieved with high postoperative satisfaction.',
      requestAssessment: 'Request Assessment',
      callNow: 'Call Now',
      benefitsTitle: 'Benefits',
      resultsTitle: 'Surgery results',
      benefits: [
        'Defined and natural masculine chest',
        'High postoperative satisfaction',
        'Technique adapted to gynecomastia grade',
        'Liposuction and/or glandular excision as needed',
        'Minimal and discreet scars',
        'Recovery in 1-2 weeks',
      ],
      quote: '"Gynecomastia is one of the surgeries with the highest satisfaction rate. Patients regain confidence and wellbeing from the first weeks."',
      classificationTitle: 'Classification',
      gradesTitle: 'Grades of Gynecomastia',
      grades: [
        { titulo: 'Grade I - Mild', descripcion: 'Small breast enlargement without excess skin. Mainly treated with liposuction.' },
        { titulo: 'Grade II - Moderate', descripcion: 'Moderate enlargement requiring liposuction combined with glandular tissue excision.' },
        { titulo: 'Grade III - Severe', descripcion: 'Significant enlargement with excess skin. May require additional skin resection.' },
      ],
      ctaTitle: 'Want more information?',
      ctaDesc: 'Request a personalized assessment consultation.',
      contact: 'Contact',
    },
    fr: {
      eyebrow: 'Chirurgie Corporelle',
      title: 'Gynécomastie',
      description: 'Chirurgie de réduction mammaire masculine. Par liposuccion et/ou excision glandulaire selon le grade, on obtient un torse masculin défini avec une grande satisfaction postopératoire.',
      requestAssessment: 'Demander une Évaluation',
      callNow: 'Appeler Maintenant',
      benefitsTitle: 'Avantages',
      resultsTitle: 'Résultats de la chirurgie',
      benefits: [
        'Torse masculin défini et naturel',
        'Haute satisfaction postopératoire',
        'Technique adaptée au grade de gynécomastie',
        'Liposuccion et/ou excision glandulaire selon le cas',
        'Cicatrices minimes et discrètes',
        'Récupération en 1-2 semaines',
      ],
      quote: '"La gynécomastie est l\'une des chirurgies avec le taux de satisfaction le plus élevé. Les patients retrouvent confiance et bien-être dès les premières semaines."',
      classificationTitle: 'Classification',
      gradesTitle: 'Grades de Gynécomastie',
      grades: [
        { titulo: 'Grade I - Léger', descripcion: 'Petit élargissement mammaire sans excès de peau. Traité principalement par liposuccion.' },
        { titulo: 'Grade II - Modéré', descripcion: 'Élargissement modéré nécessitant liposuccion combinée à l\'excision du tissu glandulaire.' },
        { titulo: 'Grade III - Sévère', descripcion: 'Élargissement significatif avec excès de peau. Peut nécessiter une résection cutanée supplémentaire.' },
      ],
      ctaTitle: 'Vous voulez plus d\'informations?',
      ctaDesc: 'Demandez une consultation d\'évaluation personnalisée.',
      contact: 'Contacter',
    },
    pt: {
      eyebrow: 'Cirurgia Corporal',
      title: 'Ginecomastia',
      description: 'Cirurgia de redução da mama masculina. Mediante lipoaspiração e/ou extirpação glandular segundo o grau, consegue-se um peito masculino definido com alta satisfação pós-operatória.',
      requestAssessment: 'Solicitar Avaliação',
      callNow: 'Ligar Agora',
      benefitsTitle: 'Benefícios',
      resultsTitle: 'Resultados da cirurgia',
      benefits: [
        'Peito masculino definido e natural',
        'Alta satisfação pós-operatória',
        'Técnica adaptada ao grau de ginecomastia',
        'Lipoaspiração e/ou extirpação glandular conforme o caso',
        'Cicatrizes mínimas e discretas',
        'Recuperação em 1-2 semanas',
      ],
      quote: '"A ginecomastia é uma das cirurgias com maior índice de satisfação. Os pacientes recuperam confiança e bem-estar desde as primeiras semanas."',
      classificationTitle: 'Classificação',
      gradesTitle: 'Graus de Ginecomastia',
      grades: [
        { titulo: 'Grau I - Leve', descripcion: 'Pequeno aumento mamário sem excesso de pele. Tratado principalmente com lipoaspiração.' },
        { titulo: 'Grau II - Moderada', descripcion: 'Aumento moderado que requer lipoaspiração combinada com extirpação de tecido glandular.' },
        { titulo: 'Grau III - Severa', descripcion: 'Aumento significativo com excesso de pele. Pode requerer ressecção cutânea adicional.' },
      ],
      ctaTitle: 'Deseja mais informações?',
      ctaDesc: 'Solicite uma consulta de avaliação personalizada.',
      contact: 'Contatar',
    },
  };

  const t = texts[lang] || texts.es;

  return (
    <div className="min-h-screen bg-[#FDFBF8]">
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#FDFBF8]">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection animation="fadeRight">
              <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{t.eyebrow}</p>
              <h1 className="font-serif text-5xl md:text-6xl text-[#1C1C1A] mb-6 leading-tight">{t.title}</h1>
              <div className="w-20 h-[2px] bg-[#B8975A] mb-8"></div>
              <p className="text-lg text-[#4A4845] leading-relaxed mb-8">{t.description}</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contacto" className="inline-flex items-center justify-center gap-2 bg-[#B8975A] text-white px-8 py-4 text-sm tracking-wider uppercase hover:bg-[#96784a] transition-colors">
                  {t.requestAssessment}
                  <ArrowRight size={16} />
                </Link>
                <a href="tel:+34952764547" className="inline-flex items-center justify-center gap-2 border border-[#1C1C1A] text-[#1C1C1A] px-8 py-4 text-sm tracking-wider uppercase hover:bg-[#1C1C1A] hover:text-white transition-colors">
                  <Phone size={16} />
                  {t.callNow}
                </a>
              </div>
            </AnimatedSection>
            <AnimatedSection animation="fadeLeft" className="relative">
              <div className="absolute -top-4 -left-4 w-24 h-24 border-t-2 border-l-2 border-[#B8975A]"></div>
              <img src="https://customer-assets.emergentagent.com/job_norro-rebranding/artifacts/70ixp6rq_unnamed.jpg" alt="Ginecomastia" className="w-full h-auto" />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-2 border-r-2 border-[#B8975A]"></div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Beneficios */}
      <section className="py-20 bg-[#1C1C1A]">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection animation="fadeRight">
              <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{t.benefitsTitle}</p>
              <h2 className="font-serif text-4xl text-white mb-8">{t.resultsTitle}</h2>
              <div className="space-y-4">
                {t.benefits.map((beneficio, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-6 h-6 rounded-full bg-[#B8975A]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check size={14} className="text-[#B8975A]" />
                    </div>
                    <span className="text-white/80">{beneficio}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>
            <AnimatedSection animation="fadeLeft">
              <div className="bg-[#B8975A]/10 p-10 border-l-2 border-[#B8975A]">
                <p className="font-serif text-2xl text-white italic leading-relaxed">{t.quote}</p>
                <p className="text-[#B8975A] mt-6">— Dr. Ángel Norro</p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Grados */}
      <section className="py-20 bg-[#F0EDE7]">
        <div className="container mx-auto px-6 max-w-6xl">
          <AnimatedSection animation="fadeUp" className="text-center mb-16">
            <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{t.classificationTitle}</p>
            <h2 className="font-serif text-4xl text-[#1C1C1A]">{t.gradesTitle}</h2>
          </AnimatedSection>
          <div className="grid md:grid-cols-3 gap-8">
            {t.grades.map((grado, index) => (
              <AnimatedSection key={index} animation="fadeUp" delay={index * 0.1}>
                <div className="bg-white p-8 border-b-2 border-[#B8975A] h-full">
                  <span className="text-[#B8975A]/40 font-serif text-sm">0{index + 1}</span>
                  <h3 className="font-serif text-2xl text-[#1C1C1A] mt-2 mb-4">{grado.titulo}</h3>
                  <p className="text-[#4A4845]">{grado.descripcion}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#FDFBF8]">
        <div className="container mx-auto px-6 max-w-3xl text-center">
          <AnimatedSection animation="fadeUp">
            <h2 className="font-serif text-4xl text-[#1C1C1A] mb-6">{t.ctaTitle}</h2>
            <p className="text-[#4A4845] mb-10">{t.ctaDesc}</p>
            <Link to="/contacto" className="inline-flex items-center gap-2 bg-[#B8975A] text-white px-10 py-4 text-sm tracking-wider uppercase hover:bg-[#96784a] transition-colors">
              {t.contact}
              <ArrowRight size={16} />
            </Link>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Ginecomastia;
